import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, Copy, KeyRound, Loader2, Plus, ShieldOff } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { del, get, post, qs } from '../api';
import { usePermissions } from '../auth';
import { Confirm, Empty, ErrorBox, Loading, Modal, PageHeader, useToast } from '../components/ui';
import { fmtDate, fmtRelative } from '../format';
import type { ApiKey } from '../types';
import { groupName, useGroups } from './shared';

export function ApiKeysPage() {
  const perms = usePermissions();
  const groups = useGroups();
  const qc = useQueryClient();
  const toast = useToast();
  const [groupId, setGroupId] = useState('');
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState<ApiKey | null>(null);

  const keys = useQuery({
    queryKey: ['api-keys', { groupId }],
    queryFn: async () => (await get<{ items: ApiKey[] }>(`/api-keys${qs({ groupId })}`)).items,
  });

  return (
    <>
      <PageHeader
        title="API keys"
        subtitle="Claves para el agente de carpetas y otras integraciones. Cada clave solo ve los trabajos que envía."
        actions={
          <button className="btn btn-primary" onClick={() => setCreating(true)}>
            <Plus size={16} /> Nueva API key
          </button>
        }
      />
      <div className="card">
        {perms.isSuper && (
          <div className="toolbar">
            <select className="select" value={groupId} onChange={(e) => setGroupId(e.target.value)}>
              <option value="">Todos los grupos</option>
              {groups.data?.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <ErrorBox error={keys.error} />
        {keys.isLoading ? (
          <Loading />
        ) : !keys.data?.length ? (
          <Empty icon={KeyRound} title="No hay API keys">
            Crea una para conectar el agente de carpetas.
          </Empty>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Clave</th>
                  {perms.isSuper && <th>Grupo</th>}
                  <th>Estado</th>
                  <th>Último uso</th>
                  <th>Creada</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {keys.data.map((k) => (
                  <tr key={k.id}>
                    <td className="cell-main">{k.name}</td>
                    <td className="mono faint">ocr_{k.prefix}_••••••</td>
                    {perms.isSuper && <td>{groupName(groups.data, k.groupId)}</td>}
                    <td>
                      {k.revokedAt ? (
                        <span className="badge badge-cancelled" title={fmtDate(k.revokedAt)}>
                          Revocada
                        </span>
                      ) : (
                        <span className="badge badge-done">Activa</span>
                      )}
                    </td>
                    <td>{fmtRelative(k.lastUsedAt)}</td>
                    <td>{fmtDate(k.createdAt)}</td>
                    <td>
                      <div className="row-actions">
                        {!k.revokedAt && (
                          <button className="btn btn-sm btn-danger" onClick={() => setRevoking(k)}>
                            <ShieldOff size={14} /> Revocar
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {creating && <CreateKeyModal onClose={() => setCreating(false)} />}
      {revoking && (
        <Confirm
          title="Revocar API key"
          message={
            <>
              ¿Revocar <strong>{revoking.name}</strong>? Los agentes que la usen dejarán de funcionar al momento. No se
              puede deshacer.
            </>
          }
          confirmLabel="Revocar"
          onClose={() => setRevoking(null)}
          onConfirm={async () => {
            await del(`/api-keys/${revoking.id}`);
            await qc.invalidateQueries({ queryKey: ['api-keys'] });
            toast('API key revocada');
          }}
        />
      )}
    </>
  );
}

function CreateKeyModal({ onClose }: { onClose: () => void }) {
  const perms = usePermissions();
  const groups = useGroups();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [groupId, setGroupId] = useState('');
  const [created, setCreated] = useState<ApiKey | null>(null);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const key = await post<ApiKey>('/api-keys', { name, groupId: perms.isSuper ? Number(groupId) : undefined });
      setCreated(key);
      await qc.invalidateQueries({ queryKey: ['api-keys'] });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(created!.key!);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (created) {
    return (
      <Modal
        title="API key creada"
        onClose={onClose}
        footer={
          <button className="btn btn-primary" onClick={onClose}>
            Hecho
          </button>
        }
      >
        <div className="alert alert-warn">Copia la clave ahora. Por seguridad no se volverá a mostrar.</div>
        <div>
          <strong>{created.name}</strong>
        </div>
        <div className="secret">
          <code>{created.key}</code>
          <button className="btn btn-sm" onClick={() => void copy()}>
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? 'Copiada' : 'Copiar'}
          </button>
        </div>
        <p className="muted" style={{ marginTop: 0 }}>
          Úsala en la cabecera <code>X-API-Key</code> o en la configuración del agente de carpetas.
        </p>
      </Modal>
    );
  }

  return (
    <Modal
      title="Nueva API key"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            Cancelar
          </button>
          <button className="btn btn-primary" form="key-form" disabled={busy}>
            {busy && <Loader2 size={14} className="spin" />}
            Crear
          </button>
        </>
      }
    >
      <form id="key-form" onSubmit={submit}>
        <ErrorBox error={error} />
        <div className="field">
          <label htmlFor="k-name">Nombre</label>
          <input id="k-name" className="input" required maxLength={120} autoFocus placeholder="p. ej. Escáner recepción" value={name} onChange={(e) => setName(e.target.value)} />
          <span className="hint">Para reconocer dónde se usa.</span>
        </div>
        {perms.isSuper && (
          <div className="field">
            <label htmlFor="k-group">Grupo</label>
            <select id="k-group" className="select" required value={groupId} onChange={(e) => setGroupId(e.target.value)}>
              <option value="">Elige un grupo</option>
              {groups.data?.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </form>
    </Modal>
  );
}
